import _, { get, isNil, set } from 'lodash';
import $$api from 'models/api';
import $$auth from 'models/auth';
import $$ep from 'models/ep';
import navigate from 'models/router-gate';
import { $global, storage } from 'models/store-global';

import AlertsService from 'services/AlertsService';
import { PRELIMINARY } from 'services/AuthService';
import FeatureThresholdService from 'services/FeatureThresholdService';
import LightEngineService from 'services/LightEngineService';
import { filterTrueValues, objectFlatten, opposite } from 'utils/common-helper';

import { eventDispatch } from 'hooks/useEventListener';

import { MyStore } from 'types/Store';
import { type Answers } from 'types/zod/answers';
import { Participant } from 'types/zod/participant';
import { type Plan } from 'types/zod/plan';
import { type EstatePlanProductChoice, type ProductChoice } from 'types/zod/product-choice';

const EstatePlanService = {
    marriedPath: 'initial_questions.common_questions.the_basics.group.married_or_have_life_partner' as const,

    withSpousePath: 'initial_questions.common_questions.the_basics.group.create_ep_with_spouse' as const,

    withSpouseYesChoicePath: 'initial_questions.specify_spouse_in_ep.yes_choice' as const,

    chooseProductPath: 'initial_questions.common_questions.choose_product' as const,

    assessmentSectionPath: 'initial_questions.common_questions' as const,

    assessmentPaths: [] as string[],

    getPlans: (): Plan[] => {
        return $global.map((state) => state.temp?.plans || []).getState();
    },

    getOriginatorDocs: (answers: Answers) => {
        const docsToPaths = [
            { doc: 'GDPOA', path: 'health_decisions.include_gdpoa.yes_choice' },
            { doc: 'HCPOA', path: 'health_decisions.include_health_care_decision_docs.yes_choice.hcpoa' },
            { doc: 'LW', path: 'health_decisions.include_health_care_decision_docs.yes_choice.lw' },
            { doc: 'AHCD', path: 'health_decisions.include_health_care_decision_docs.yes_choice.ahcd' },
            { doc: 'HIPAA', path: 'health_decisions.include_hipaa.yes_choice' },
        ];
        return EstatePlanService.getDocs(answers, docsToPaths);
    },

    getSpouseDocs: (answers: Answers) => {
        const docsToPaths = [
            { doc: 'GDPOA', path: 'health_decisions.include_spouse_gdpoa.yes_choice' },
            { doc: 'HCPOA', path: 'health_decisions.include_spouse_health_care_decision_docs.yes_choice.hcpoa' },
            { doc: 'LW', path: 'health_decisions.include_spouse_health_care_decision_docs.yes_choice.lw' },
            { doc: 'AHCD', path: 'health_decisions.include_spouse_health_care_decision_docs.yes_choice.ahcd' },
            { doc: 'HIPAA', path: 'health_decisions.include_spouse_hipaa.yes_choice' },
        ];
        return EstatePlanService.getDocs(answers, docsToPaths);
    },

    getDocs: (answers: Answers, docsToPaths: any[]) => {
        const docs = [] as string[];
        docsToPaths.forEach((docToPath) => {
            if (get(answers, docToPath.path)) {
                docs.push(docToPath.doc);
            }
        });
        return docs;
    },

    getProductContext: (productName: string, longVersion?: boolean) => {
        return {
            simple_product_choice: longVersion ? 'simple_will' : 'sw',
            custom_product_choice: longVersion ? 'custom_will' : 'cw',
            trust_product_choice: longVersion ? 'living_trust' : 'lt',
        }?.[productName];
    },

    getSelectedProductContext: (longVersion?: boolean) => {
        const selectedProduct = $$ep.$selectedProduct.getState();
        return {
            simple_product_choice: longVersion ? 'simple_will' : 'sw',
            custom_product_choice: longVersion ? 'custom_will' : 'cw',
            trust_product_choice: longVersion ? 'living_trust' : 'lt',
        }[selectedProduct || 'simple_product_choice'];
    },

    setEPWithoutSpouse: async (qInstanceId: number) => {
        const answers = _($global.map((state) => state.answers?.[qInstanceId]).getState())
            .set(EstatePlanService.marriedPath, false)
            .set(EstatePlanService.withSpousePath, false)
            .omit(EstatePlanService.withSpouseYesChoicePath)
            .value();
        await $$api.answers.putAnswersFx({ id: qInstanceId, answers });
        // TODO: Get rid of props drilling and use storage for answersForConditions
        eventDispatch('UPDATE_ANSWERS_FOR_CONDITIONS', answers);
    },

    chooseProduct: async (productName: ProductChoice) => {
        const qInstanceId = EstatePlanService.getQInstanceId();
        if (!qInstanceId) {
            return;
        }

        const answers = $$ep.stores.$epAnswers
            .map((answers) =>
                _(answers)
                    .omit(EstatePlanService.chooseProductPath)
                    .set(`${EstatePlanService.chooseProductPath}.${productName}`, true)
                    .value(),
            )
            .getState();

        await $$api.answers.putAnswersFx({ id: qInstanceId, answers });
        // TODO: Get rid of props drilling and use storage for answersForConditions
        eventDispatch('UPDATE_ANSWERS_FOR_CONDITIONS', answers);
    },

    getQInstanceId: () => {
        const { questionnaire_instance_id } = $$ep.stores.$epQInstance.getState() || {};
        return questionnaire_instance_id;
    },

    getAnswerByPath: (path: string): any => {
        return $$ep.stores.$epAnswers.map((answers) => get(answers || {}, path)).getState();
    },

    getPlanCode: (): Plan['plan_code'] => {
        const plans = EstatePlanService.getPlans();
        const productChoice = $$ep.$selectedProduct.getState();
        const isSingle = $$ep.$isSingle.getState();
        const plan = _.find(plans, { product_choice: productChoice, couple: !isSingle }) as unknown as Plan;
        return (plan || plans[0]).plan_code;
    },

    updateEstatePlan: async () => {
        const qInstance = $$ep.stores.$epQInstance.getState();
        if (!qInstance) {
            return;
        }
        const { questionnaire_type_code, questionnaire_instance_id, state_code } = qInstance || {};
        const isLT = $$ep.$selectedProduct.getState() === 'trust_product_choice';
        if (isLT) {
            navigate(`/document/${questionnaire_type_code}/${state_code}/${questionnaire_instance_id}`);
            return;
        } else {
            await Promise.all([
                $$api.qInstances.setStatusFx({ id: questionnaire_instance_id, status: 'In_Progress' }),
                $$api.answers.putAnswersFx({ id: questionnaire_instance_id, path: 'update', answers: {} }),
            ]);
            await EstatePlanService.loadEstatePlan();
            navigate('/dashboard/in-progress');
            return;
        }
    },

    isToBePayedAtTheEnd: (): boolean => {
        const me = $$auth.$currentParticipant.getState();
        if (!me) {
            return false;
        }
        return ![PRELIMINARY, 'temp'].includes(me.last_name) && !FeatureThresholdService.canPayAtTheBeginning();
    },

    getQuestionnaire: () => {
        return $$ep.stores.$epQuestionnaire.getState();
    },

    // TODO: Must be replaced with _on condition checking
    isAssessmentQuestionVisible: (questionPath: string): boolean => {
        return LightEngineService.isQuestionVisible(questionPath);
    },

    getQuestionByPath: (questionPath: string): any => {
        const questionnaire = EstatePlanService.getQuestionnaire();
        return get(questionnaire, questionPath);
    },

    getFirstAssessmentGroupPaths: (): string[] => {
        const questionnaire = EstatePlanService.getQuestionnaire();
        const firstAssessmentGroupPath = 'initial_questions.common_questions.the_basics.group';
        const group = get(questionnaire, firstAssessmentGroupPath, {});
        return Object.keys(group)
            .filter((i) => !i.startsWith('_'))
            .map((i) => `${firstAssessmentGroupPath}.${i}`)
            .filter(EstatePlanService.isAssessmentQuestionVisible);
    },

    isFirstAssessmentGroupComplete: (): boolean => {
        const answers = $$ep.stores.$epAnswers.getState();
        if (!answers) {
            return true;
        }
        const questionsPaths = EstatePlanService.getFirstAssessmentGroupPaths();
        const groupAnswers = questionsPaths.map((i) => get(answers, i));
        return groupAnswers.every(_.isBoolean);
    },

    getAssessmentPaths: (): string[] => {
        if (EstatePlanService.assessmentPaths.length) {
            return EstatePlanService.assessmentPaths;
        }
        const questionnaire = EstatePlanService.getQuestionnaire();
        const assessmentSectionQuestions = get(questionnaire, 'initial_questions.common_questions', {});
        EstatePlanService.assessmentPaths = Object.entries(objectFlatten(assessmentSectionQuestions))
            .filter((i) => i[1] === 'label')
            .map((i) => 'initial_questions.common_questions.' + i[0].split('._widget')[0]);
        return EstatePlanService.assessmentPaths;
    },

    getCongratulationAnswers: (answers: Answers): Object => {
        const congratulationAnswersAsString = get(answers, 'hidden.last_congratulated', '{}');
        try {
            const congratulationAnswers = JSON.parse(congratulationAnswersAsString);
            if (typeof congratulationAnswers !== 'object') {
                return new Error('congratulationAnswers is not an object');
            }
            return congratulationAnswers;
        } catch (error) {
            AlertsService.showError('Error while parsing congratulation answers');
            console.log(error);
            return {};
        }
    },

    saveCongratulatedAnswers: async () => {
        const qInstanceId = EstatePlanService.getQInstanceId();
        if (!qInstanceId) {
            return;
        }
        const paths = EstatePlanService.getAssessmentPaths();
        const assessmentAnswers = _.pick($$ep.stores.$epAnswers.getState() || {}, paths);
        const assessmentAnswersAsString = JSON.stringify(assessmentAnswers);
        storage.set(
            (store): MyStore => ({
                ...store,
                answers: {
                    ...(store.answers || {}),
                    [qInstanceId]: {
                        ...(store.answers?.[qInstanceId] || {}),
                        hidden: {
                            ...(store.answers?.[qInstanceId]?.hidden || {}),
                            last_congratulated: assessmentAnswersAsString,
                        },
                    },
                },
            }),
        );
        await $$api.answers.putAnswersFx({
            id: qInstanceId,
            answers: $$ep.stores.$epAnswers.getState() || {},
        });
        return assessmentAnswers;
    },

    getCongratulationPaths: (answers: Answers): Record<string, boolean> => {
        const paths = EstatePlanService.getAssessmentPaths();
        const assessmentAnswers = _.pick(answers || {}, paths);
        return objectFlatten(filterTrueValues(assessmentAnswers));
    },

    setQuestionAnswerByPath: (questionPath: string, answer: Answers): void => {
        const qInstanceId = EstatePlanService.getQInstanceId();
        storage.set(
            (store): MyStore => ({
                ...store,
                answers: {
                    ...(store.answers || {}),
                    [qInstanceId || -1]: set(store.answers?.[qInstanceId || -1], questionPath, answer),
                },
            }),
        );
    },

    saveAnswers: async (): Promise<void> => {
        const qInstanceId = EstatePlanService.getQInstanceId();
        if (!qInstanceId) {
            return;
        }
        const answers = $$ep.stores.$epAnswers.getState() || {};
        await $$api.answers.putAnswersFx({ id: qInstanceId, answers });
    },

    answerForFirstProductQuestion: async (productChoice: EstatePlanProductChoice) => {
        if (productChoice === 'simple_product_choice') {
            return;
        }
        const paths: Omit<Record<EstatePlanProductChoice, string>, 'simple_product_choice'> = {
            custom_product_choice: 'originator_beneficiaries.group.want_to_leave_specific_assets',
            trust_product_choice: 'goverment_involvement.group.estab_living_trust',
        };
        const productQuestionPath = `initial_questions.common_questions.${paths[productChoice]}`;
        EstatePlanService.setQuestionAnswerByPath(productQuestionPath, true);
        await EstatePlanService.saveAnswers();
    },

    loadEstatePlan: async () => {
        if ($global.map((state) => state.temp?.estatePlanLoading).getState()) {
            return;
        }
        try {
            storage.set((store): MyStore => ({ ...store, temp: { ...(store.temp || {}), estatePlanLoading: true } }));
            storage.set((store): MyStore => ({ ...store, temp: { ...(store.temp || {}), estatePlanLoaded: true } }));
            const [community, references] = await Promise.all([
                $$api.participants.getCommunityFx(undefined),
                $$api.participants.getParticipantReferencesFx(),
            ]);

            if (isNil($global.map((state) => state.temp?.selected_community_participant).getState())) {
                storage.set(
                    (store): MyStore => ({
                        ...store,
                        temp: {
                            ...(store.temp || {}),
                            selected_community_participant: community.tabs[0],
                        },
                    }),
                );
            }

            await $$api.qInstances.getInstancesFx(undefined);
            const qInstanceId = EstatePlanService.getQInstanceId();

            if (qInstanceId) {
                if (
                    $global
                        .map((state) => state.childrenLoaded)
                        .map(Boolean)
                        .map(opposite)
                        .getState()
                ) {
                    const children = await $$api.qInstances.loadChildrenFx(+qInstanceId);
                    storage.set(
                        (state): MyStore => ({
                            ...state,
                            answers: {
                                ...(state.answers || {}),
                                ...children.answers,
                            },
                            questionnaires: {
                                ...(state.questionnaires || {}),
                                ...children.questionnaires,
                            },
                            childrenLoaded: true,
                        }),
                    );
                }
            }

            return $$ep.stores.$epQInstance.getState();
        } catch (e) {
        } finally {
            storage.set((store): MyStore => ({ ...store, temp: { ...(store.temp || {}), estatePlanLoaded: true } }));
            storage.set((store): MyStore => {
                const { estatePlanLoading, ...temp } = store.temp || {};
                return { ...store, temp };
            });
        }
    },

    getWasGeneratedWithCharity: (answers: Answers): boolean => {
        const generatedWithCharityPath = 'hidden.was_generated_with_charity_advisor';
        return !isNil(get(answers, generatedWithCharityPath));
    },

    getIsCharityLinked: (participants: Participant[]): boolean => {
        return participants.some(
            (i) => i.observer_relationship === 'FinancialAdvisor' && i.participant_type_code === 'Charity',
        );
    },

    getLoopPaths: (answers: Answers, path: string): string[] => {
        const pathToLoop = path.split('_loop')[0];
        const loop = get(answers, pathToLoop + '_loop', []);

        const loopPaths: string[] = loop.map((_i: object, index: number) =>
            path.replace('_loop[*]', `_loop[${index}]`),
        );

        return loopPaths;
    },

    getIsCharitySelected: (answers: Answers): boolean => {
        const chartityRecPath =
            'specific_distros.any_specific_distros_orig.specific_distros_orig.distributions._loop[*].distros_type.distribution_recipient.charity_rec.charity_name._participant';
        const chartityBenePath =
            'bene_sel_method.list_of_benes_distr_by_proportion.list_bene_proportion._loop[*].bene_proportion.bene_type.charity_bene.bene_name._participant';
        const pathsToCheck = [
            ...EstatePlanService.getLoopPaths(answers, chartityRecPath),
            ...EstatePlanService.getLoopPaths(answers, chartityBenePath),
        ];

        return pathsToCheck.some((i) => {
            const participant = get(answers, i);
            return participant && participant.participant_type_code === 'Charity';
        });
    },
};

export default EstatePlanService;
